import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Form, Row, Col, Spinner, Modal, Button } from 'react-bootstrap';
import useAxios from "../utils/useAxios";
import '../styles/ConfigDatasheet.css';
// ... EditableScalarCell, LookupPlot, LookupTable, etc. ...
const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

function EditableScalarCell({ param, isChanged, onFinalChange }) {
  const initialValue = param.value ?? param.default_value;
  const [inputValue, setInputValue] = useState(initialValue);

  // Sync with external changes (like a global reset)
  useEffect(() => {
    setInputValue(param.value ?? param.default_value);
  }, [param.value, param.default_value]);

  const handleBlur = () => {
    // On blur, parse and clamp the number, then send final value to parent
    if (param.display_type === 'number') {
      const rawValue = parseFloat(inputValue);
      const clampedValue = (param.min != null && param.max != null)
        ? clamp(rawValue, param.min, param.max)
        : rawValue;
      onFinalChange(param, clampedValue);
    }
  };

  switch (param.display_type) {
    case 'bool':
      return (
        <Form.Select
          className={`scalar-select ${isChanged ? 'value-changed' : ''}`}
          value={String(inputValue)}
          onChange={(e) => onFinalChange(param, e.target.value === 'true')}
        >
          <option value="true">True</option>
          <option value="false">False</option>
        </Form.Select>
      );
    case 'string':
      return (
        <input
          type="text"
          className={`scalar-input ${isChanged ? 'value-changed' : ''}`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={() => onFinalChange(param, inputValue)}
        />
      );
    case 'number':
    default:
      return (
        <input
          type="number"
          className={`scalar-input ${isChanged ? 'value-changed' : ''}`}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onBlur={handleBlur}
        />
      );
  }
}

function LookupPlot({ data, axisLabels }) {
  const [xValues, yValues] = data;
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, content: '' });

  if (!xValues || xValues.length < 2) {
    return <div style={{padding: '20px', textAlign: 'center', color: '#888'}}>Not enough data to draw a plot.</div>;
  }

  // --- Calculations for plot dimensions and scales ---
  const svgWidth = 500;
  const svgHeight = 300;
  const margin = { top: 20, right: 20, bottom: 40, left: 50 };
  const plotWidth = svgWidth - margin.left - margin.right;
  const plotHeight = svgHeight - margin.top - margin.bottom;

  const minX = Math.min(...xValues);
  const maxX = Math.max(...xValues);
  const minY = Math.min(...yValues);
  const maxY = Math.max(...yValues);

  const scaleX = (val) => margin.left + ((val - minX) / (maxX - minX || 1)) * plotWidth;
  const scaleY = (val) => margin.top + plotHeight - ((val - minY) / (maxY - minY || 1)) * plotHeight;

  const generateTicks = (min, max, tickCount) => {
    if (min === max) return [min];

    const range = max - min;
    const rawStep = range / (tickCount - 1);

    // Calculate steps
    const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const normalizedStep = rawStep / magnitude;

    let niceNormalizedStep;
    if (normalizedStep < 1.5) niceNormalizedStep = 1;
    else if (normalizedStep < 3) niceNormalizedStep = 2;
    else if (normalizedStep < 7) niceNormalizedStep = 5;
    else niceNormalizedStep = 10;

    const niceStep = niceNormalizedStep * magnitude;

    const start = Math.floor(min / niceStep) * niceStep;
    const end = Math.ceil(max / niceStep) * niceStep;

    const ticks = [];
    for (let i = start; i <= end; i += niceStep) {
      // Use toPrecision to avoid floating point errors
      ticks.push(Number(i.toPrecision(12)));
    }
    return ticks;
  };

  const xTicks = generateTicks(minX, maxX, 5);
  const yTicks = generateTicks(minY, maxY, 5);

  // --- Logic for the data line and points ---
  const linePath = xValues.map((val, i) => `${i === 0 ? 'M' : 'L'} ${scaleX(val)} ${scaleY(yValues[i])}`).join(' ');
  const dataPoints = xValues.map((val, i) => ({
    cx: scaleX(val),
    cy: scaleY(yValues[i]),
    xVal: val,
    yVal: yValues[i],
  }));

  // Handlers for showing/hiding the tooltip ---
  const handleMouseOver = (event, point) => {
    setTooltip({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      content: `${axisLabels.x}: ${point.xVal}\n${axisLabels.y}: ${point.yVal.toFixed(2)}`
    });
  };

  const handleMouseOut = () => {
    setTooltip({ ...tooltip, visible: false });
  };

  return (
    <div style={{ marginTop: '20px', position: 'relative' }}>
      <h4 style={{ margin: 0, fontSize: '14px', color: '#555' }}>Data Plot</h4>
        <svg width={svgWidth} height={svgHeight}>
            {/* Render Grid Lines */}
            {yTicks.map(tick => (
              <line key={`y-grid-${tick}`} x1={margin.left} y1={scaleY(tick)} x2={margin.left + plotWidth} y2={scaleY(tick)} stroke="#e0e0e0" />
            ))}
            {xTicks.map(tick => (
              <line key={`x-grid-${tick}`} x1={scaleX(tick)} y1={margin.top} x2={scaleX(tick)} y2={margin.top + plotHeight} stroke="#e0e0e0" />
            ))}

            {/* X and Y Axes */}
            <line x1={margin.left} y1={margin.top} x2={margin.left} y2={margin.top + plotHeight} stroke="#333" />
            <line x1={margin.left} y1={margin.top + plotHeight} x2={margin.left + plotWidth} y2={margin.top + plotHeight} stroke="#333" />

            {/* Render Axis Tick Labels */}
            {yTicks.map(tick => (
              <text key={`y-label-${tick}`} x={margin.left - 5} y={scaleY(tick)} dy="3" textAnchor="end" fontSize="10">{tick}</text>
            ))}
            {xTicks.map(tick => (
              <text key={`x-label-${tick}`} x={scaleX(tick)} y={margin.top + plotHeight + 15} textAnchor="middle" fontSize="10">{tick}</text>
            ))}

            {/* Axis Title Labels */}
            <text x={margin.left + plotWidth / 2} y={svgHeight - 5} textAnchor="middle" fontSize="12">{axisLabels.x}</text>
            <text transform={`rotate(-90)`} x={-(margin.top + plotHeight / 2)} y={15} textAnchor="middle" fontSize="12">{axisLabels.y}</text>

            {/* Data Line */}
            <path d={linePath} fill="none" stroke="#007bff" strokeWidth="2" />

            {/* Data Points with hover events */}
            {dataPoints.map((p, i) => (
              <circle
                key={i}
                cx={p.cx}
                cy={p.cy}
                r="4"
                fill="#007bff"
                onMouseEnter={(e) => handleMouseOver(e, p)}
                onMouseLeave={handleMouseOut}
                style={{ cursor: 'pointer' }}
              />
            ))}
        </svg>

        {/* Render the tooltip div */}
        {tooltip.visible && (
          <div className="plot-tooltip" style={{ left: tooltip.x, top: tooltip.y }}>
            {tooltip.content}
          </div>
        )}
    </div>
  );
}

function LookupTable({ param, onChange }) {
  // Get min/max from the Lookup object for easy access
  const minX = param.Lookup?.min?.[0];
  const maxX = param.Lookup?.max?.[0];
  const minY = param.Lookup?.min?.[1];
  const maxY = param.Lookup?.max?.[1];
  const initialValues = Array.isArray(param.value) && param.value.length === 2
    ? param.value
    : [[], []];

  const [values, setValues] = useState(initialValues);
  const [copyFeedback, setCopyFeedback] = useState('');

  const isChangeable = param.modifiable ?? param.isChangeableForProject ?? false;

  useEffect(() => {
    // When the param.value from the parent changes (e.g., on reset),
    // update the internal state of this component.
    const newInitialValues = Array.isArray(param.value) && param.value.length === 2
      ? param.value
      : [[], []];
    setValues(newInitialValues);
  }, [param.value]);

  // Handles changes in the new horizontal layout
  const handleCellChange = (axis, index, newValue) => {
    const newValues = JSON.parse(JSON.stringify(values));
    const targetArrayIndex = axis === 'x' ? 0 : 1;

    newValues[targetArrayIndex][index] = newValue;
    setValues(newValues); // Update local state with the string

  };

  // Handles blur event to finalize, parse, and clamp the value
  const handleCellBlur = (axis, index) => {
    const newValues = JSON.parse(JSON.stringify(values));
    const targetArrayIndex = axis === 'x' ? 0 : 1;
    const rawValue = newValues[targetArrayIndex][index];

    let numericValue = parseFloat(rawValue);

    if (isNaN(numericValue)) {
      numericValue = 0; // Default to 0 if invalid
    }

    // Clamp the valid number
    if (axis === 'x') {
      numericValue = clamp(numericValue, minX, maxX);
    } else {
      numericValue = clamp(numericValue, minY, maxY);
    }

    newValues[targetArrayIndex][index] = numericValue;

    setValues(newValues); // Update local state to the numeric, clamped value
    onChange(param, newValues);
  };

  const handlePaste = async () => {
    try {
      const pastedText = await navigator.clipboard.readText();
      const pastedRows = pastedText.trim().split(/\r?\n/).map(row => row.split('\t'));
      if (pastedRows.length === 0) return;

      const newValues = JSON.parse(JSON.stringify(values));
      const colsToUpdate = Math.min(pastedRows[0].length, newValues[0].length);

      for (let i = 0; i < colsToUpdate; i++) {
        const xVal = parseFloat(pastedRows[0][i]);
        if (!isNaN(xVal)) newValues[0][i] = clamp(xVal, minX, maxX);
      }
      if (pastedRows.length > 1) {
        for (let i = 0; i < colsToUpdate; i++) {
          const yVal = parseFloat(pastedRows[1][i]);
          if (!isNaN(yVal)) newValues[1][i] = clamp(yVal, minY, maxY);
        }
      }

      // Update both local state and parent state
      setValues(newValues);
      onChange(param, newValues);

    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert("Could not read from clipboard. Please ensure you've granted permission.");
    }
  };

  const handleCopy = () => {
    const datasetString = `Dataset:\t${param.dataset}`;
    const briefString = `Brief:\t${param.brief}`;

    const xRowString = [headers.x, ...(values[0] || [])].join('\t');
    const yRowString = [headers.y, ...(values[1] || [])].join('\t');

    const fullStringForClipboard = [
      datasetString,
      briefString,
      '',
      xRowString,
      yRowString
    ].join('\n');

    navigator.clipboard.writeText(fullStringForClipboard).then(() => {
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    });
  };

  const headers = {
    x: `${param.Lookup?.x?.name || 'x'} (${param.Lookup?.x?.unit || ''})`,
    y: `${param.Lookup?.y?.name || 'y'} (${param.Lookup?.y?.unit || ''})`,
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', color: '#555' }}>Table Values</h4>
        <div style={{display: 'flex', gap: '8px'}}>
          {/* Paste button added */}
          <button onClick={handlePaste} className="btn-copy-paste" disabled={!isChangeable}>Paste</button>
          <button onClick={handleCopy} className="btn-copy-paste">Copy to clipboard</button>
          {copyFeedback && <span style={{ marginLeft: '10px', color: 'green', fontSize: '12px' }}>{copyFeedback}</span>}
        </div>
      </div>
      {/* Min/Max Legend */}
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', display: 'flex', gap: '15px' }}>
        <strong>Ranges:</strong>
        <span>{headers.x}: [{minX}, {maxX}]</span>
        <span>{headers.y}: [{minY}, {maxY}]</span>
      </div>
      {/* Table is now rendered horizontally */}
      <div style={{ overflowX: 'auto' }}>
        <table className="lookup-grid">
          <tbody>
            {/* X-axis Row */}
            <tr>
              <th>{headers.x}</th>
              {(values[0] || []).map((x, index) => (
                <td key={index}>
                  <input
                    type="text"
                    value={x}
                    onChange={e => handleCellChange('x', index, e.target.value)}
                    onBlur={() => handleCellBlur('x', index)}
                    readOnly={!isChangeable}
                  />
                </td>
              ))}
            </tr>
            {/* Y-axis Row */}
            <tr>
              <th>{headers.y}</th>
              {(values[1] || []).map((y, index) => (
                <td key={index}>
                  <input
                    type="text"
                    value={y}
                    onChange={e => handleCellChange('y', index, e.target.value)}
                    onBlur={() => handleCellBlur('y', index)}
                    readOnly={!isChangeable}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <LookupPlot data={values.map(arr => arr.map(v => parseFloat(v) || 0))} axisLabels={headers} />
    </div>
  );
}

function Lookup2DTable({ param, onChange }) {

  const initialValue = Array.isArray(param.value) && param.value.length === 3
    ? param.value
    : [[], [], [[]]];
  const [xValues, setXValues] = useState(initialValue[0]); // e.g., soc
  const [yValues, setYValues] = useState(initialValue[1]); // e.g., temp
  const [zMatrix, setZMatrix] = useState(initialValue[2]);
  const [copyFeedback, setCopyFeedback] = useState('');

// Y-axis (rows) corresponds to index 0
  const minY = param.Lookup?.min?.[0];
  const maxY = param.Lookup?.max?.[0];

  // X-axis (columns) corresponds to index 1
  const minX = param.Lookup?.min?.[1];
  const maxX = param.Lookup?.max?.[1];

  // Z-axis (cells) corresponds to index 2
  const minZ = param.Lookup?.min?.[2];
  const maxZ = param.Lookup?.max?.[2];

  const isChangeable = param.modifiable ?? param.isChangeableForProject ?? false;
// Heatmap color calculation ---
  const { minArrayZ, maxArrayZ } = useMemo(() => {
    const zValuesFlat = zMatrix.flat().filter(v => typeof v === 'number');
    if (zValuesFlat.length === 0) return { minZ: 0, maxZ: 0 };
    return {
      minArrayZ: Math.min(...zValuesFlat),
      maxArrayZ: Math.max(...zValuesFlat),
    };
  }, [zMatrix]);

  const getHeatmapColor = (value) => {
    if (maxArrayZ === minArrayZ || typeof value !== 'number') return '#888'; // Gray for non-numbers or flat data
    const normalized = (value - minArrayZ) / (maxArrayZ - minArrayZ);
    // Hue from Blue (240) to Red (0)
    const hue = (1 - normalized) * 240;
    return `hsl(${hue}, 80%, 60%)`;
  };
  // Central function to update the parent component's state
  const updateParentState = (newX, newY, newZ) => {
    onChange(param, [newY, newX, newZ]);
  };

  const handleAxisChange = (axis, index, newValue) => {
    if (axis === 'x') {
      const newX = [...xValues];
      newX[index] = newValue;
      setXValues(newX);
    } else if (axis === 'y') {
      const newY = [...yValues];
      newY[index] = newValue;
      setYValues(newY);
    }

  };

  // Blur handler for axis inputs
  const handleAxisBlur = (axis, index) => {
    if (axis === 'x') {
      const newX = [...xValues];
      const rawValue = newX[index];
      let numericValue = parseFloat(rawValue);
      if (isNaN(numericValue)) numericValue = 0;
      numericValue = clamp(numericValue, minX, maxX);
      newX[index] = numericValue;
      setXValues(newX);
      updateParentState(newX, yValues, zMatrix); // Send final numeric
    } else if (axis === 'y') {
      const newY = [...yValues];
      const rawValue = newY[index];
      let numericValue = parseFloat(rawValue);
      if (isNaN(numericValue)) numericValue = 0;
      numericValue = clamp(numericValue, minY, maxY);
      newY[index] = numericValue;
      setYValues(newY);
      updateParentState(xValues, newY, zMatrix); // Send final numeric
    }
  };

  // Handler for editing a single cell in the main grid
  const handleZChange = (yIndex, xIndex, newValue) => {
    const newZ = JSON.parse(JSON.stringify(zMatrix));

    if (!newZ[yIndex]) newZ[yIndex] = [];
    newZ[yIndex][xIndex] = newValue; // Set raw string

    setZMatrix(newZ);

  };

  // Blur handler for Z-matrix inputs
  const handleZBlur = (yIndex, xIndex) => {
    const newZ = JSON.parse(JSON.stringify(zMatrix));
    const rawValue = newZ[yIndex]?.[xIndex];

    let numericValue = parseFloat(rawValue);
    if (isNaN(numericValue)) numericValue = 0;
    numericValue = clamp(numericValue, minZ, maxZ);

    if (!newZ[yIndex]) newZ[yIndex] = [];
    newZ[yIndex][xIndex] = numericValue;

    setZMatrix(newZ);
    updateParentState(xValues, yValues, newZ); // Send final numeric
  };

  // Paste handler
  const handlePaste = async () => {
    try {
      const pastedText = await navigator.clipboard.readText();
      const pastedRows = pastedText.trim().split(/\r?\n/).map(row => row.split('\t'));

      if (pastedRows.length === 0) return; // Nothing to paste

      // Check if the top-left cell [0][0] is text (e.g., "soc \ temp")
      // If it is, we assume a full-table paste (with headers)
      const isFullTablePaste = isNaN(parseFloat(pastedRows[0][0]));

      if (isFullTablePaste) {
        // --- FULL TABLE PASTE LOGIC ---
        const newX = [...xValues];
        const newY = [...yValues];
        const newZ = JSON.parse(JSON.stringify(zMatrix));

        // 1. Parse X-axis (horizontal headers) from pastedRows[0]
        const headerRow = pastedRows[0];
        // Determine number of columns to update
        const colsToUpdate = Math.min(newX.length, headerRow.length - 1);
        for (let x = 0; x < colsToUpdate; x++) {
          const xVal = parseFloat(headerRow[x + 1]); // Offset by 1 (skip "soc \ temp")
          if (!isNaN(xVal)) {
            newX[x] = clamp(xVal, minX, maxX);
          }
        }

        // 2. Parse Y-axis (vertical headers) and Z-Matrix (grid)
        // Determine number of rows to update
        const rowsToUpdate = Math.min(newY.length, pastedRows.length - 1);
        for (let y = 0; y < rowsToUpdate; y++) {
          const dataRow = pastedRows[y + 1]; // Offset by 1 (skip header row)
          if (!dataRow) continue;

          // Parse Y-header (at dataRow[0])
          const yVal = parseFloat(dataRow[0]);
          if (!isNaN(yVal)) {
            newY[y] = clamp(yVal, minY, maxY);
          }

          // Parse Z-values (starting at dataRow[1])
          for (let x = 0; x < colsToUpdate; x++) {
            const zVal = parseFloat(dataRow[x + 1]); // Offset by 1
            if (!isNaN(zVal)) {
              if (!newZ[y]) newZ[y] = []; // Ensure row exists
              newZ[y][x] = clamp(zVal, minZ, maxZ);
            }
          }
        }

        // 3. Update state
        setXValues(newX);
        setYValues(newY);
        setZMatrix(newZ);
        updateParentState(newX, newY, newZ); // Update parent with all new data

      } else {
        // --- Z-ONLY PASTE LOGIC (Original Behavior) ---
        // Top-left cell is a number, so paste only Z-values
        const newZ = JSON.parse(JSON.stringify(zMatrix));
        const rowsToUpdate = Math.min(pastedRows.length, yValues.length);
        const colsToUpdate = Math.min(pastedRows[0]?.length || 0, xValues.length);

        for (let y = 0; y < rowsToUpdate; y++) {
          for (let x = 0; x < colsToUpdate; x++) {
            const numericValue = parseFloat(pastedRows[y][x]);
            if (!isNaN(numericValue)) {
              if (!newZ[y]) newZ[y] = [];
              newZ[y][x] = clamp(numericValue, minZ, maxZ);
            }
          }
        }
        setZMatrix(newZ);
        updateParentState(xValues, yValues, newZ); // Only update Z
      }

    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert("Could not read from clipboard. Please ensure you've granted permission.");
    }
  };

  const handleCopy = () => {
    const datasetString = `Dataset:\t${param.dataset}`;
    const briefString = `Brief:\t${param.brief}`;

    // Build the copied text in the correct orientation.
    const tableHeader = [`${param.Lookup.y.name} \\ ${param.Lookup.x.name}`, ...xValues].join('\t');
    const tableRows = yValues.map((yValue, yIndex) => {
      const zRow = xValues.map((_, xIndex) => zMatrix[yIndex]?.[xIndex] ?? 0);
      return [yValue, ...zRow].join('\t');
    }).join('\n');

    const fullString = [datasetString, briefString, '', tableHeader, tableRows].join('\n');
    navigator.clipboard.writeText(fullString).then(() => {
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    });
  };

  useEffect(() => {
    const newInitialValue = Array.isArray(param.value) && param.value.length === 3
      ? param.value
      : [[], [], [[]]];
    setYValues(newInitialValue[0]);
    setXValues(newInitialValue[1]);
    setZMatrix(newInitialValue[2]);
  }, [param.value]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h4 style={{ margin: 0, fontSize: '14px', color: '#555' }}>Table Values</h4>
        <div style={{display: 'flex', gap: '8px'}}>
            {/* Paste button added */}
          <button onClick={handlePaste} className="btn-copy-paste" disabled={!isChangeable}>Paste</button>
          <button onClick={handleCopy} className="btn-copy-paste">Copy to Clipboard</button>
          {copyFeedback && <span style={{ marginLeft: '10px', color: 'green', fontSize: '12px' }}>{copyFeedback}</span>}
        </div>
      </div>
      {/* Min/Max Legend for all 3 axes */}
      <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
        <strong>Ranges:</strong>
        <span>{param.Lookup?.x.name}: [{minY}, {maxY}]</span>
        <span>{param.Lookup?.y.name}: [{minX}, {maxX}]</span>
        <span>{param.Lookup?.z.name}: [{minZ}, {maxZ}]</span>
      </div>
      {/* Heatmap Legend */}
      <div className="heatmap-legend">
        <span>Low ({minZ.toFixed(2)})</span>
        <div className="heatmap-gradient"></div>
        <span>High ({maxZ.toFixed(2)})</span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="lookup-grid" onPaste={handlePaste}>
          <thead>
            <tr>
              {/* ... (header cell) ... */}
              <th style={{ background: '#e9ecef' }}>
                {`${param.Lookup.x.name} \\ ${param.Lookup.y.name}`}
              </th>
              {xValues.map((x, index) => (
                <th key={index}>
                  <input
                    type="text"
                    value={x}
                    onChange={e => handleAxisChange('x', index, e.target.value)}
                    onBlur={() => handleAxisBlur('x', index)}
                    readOnly={!isChangeable}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {yValues.map((y, yIndex) => (
              <tr key={yIndex}>
                <th>
                  <input
                    type="text"
                    value={y}
                    onChange={e => handleAxisChange('y', yIndex, e.target.value)}
                    onBlur={() => handleAxisBlur('y', yIndex)}
                    readOnly={!isChangeable}
                  />
                </th>
                {xValues.map((_, xIndex) => {
                  const cellValue = zMatrix[yIndex]?.[xIndex] ?? '';
                  return (
                    <td
                      key={xIndex}
                      className="heatmap-cell"
                      style={{ backgroundColor: getHeatmapColor(cellValue) }}
                    >
                      <input
                        type="text"
                        value={cellValue}
                        onChange={e => handleZChange(yIndex, xIndex, e.target.value)}
                        onBlur={() => handleZBlur(yIndex, xIndex)}
                        readOnly={!isChangeable}
                      />
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const parseAndGroupVariables = (variablesByDataset) => {
  const grouped = {};
  if (!variablesByDataset) return {};

  // Iterate through the new API response structure: { "DatasetName": { "paramName": {...} } }
  for (const datasetName in variablesByDataset) {
    const variables = variablesByDataset[datasetName];
    for (const paramName in variables) {
      const paramData = variables[paramName];
      const group = paramData.display_group || 'scalar';

      if (!grouped[group]) {
        grouped[group] = [];
      }

      // This logic remains mostly the same, but is now simpler
      const currentValue = paramData.value ?? paramData.default_value;

      const flatParam = {
        name: paramName,
        dataset: datasetName, // The dataset is now the top-level key
        ...paramData,
        value: currentValue,
        default_value: paramData.default_value,
        min: paramData.range?.min,
        max: paramData.range?.max,
      };
      grouped[group].push(flatParam);
    }
  }
  return grouped;
};

function ConfigDatasheet() {
  const api = useAxios();
  const isSelectionChanging = useRef(false);

  const [isLoadingReleases, setIsLoadingReleases] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);

  // State for the selection dropdowns
  const [releaseIds, setReleaseIds] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState('');
 
  // State for Project Selection ---
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  // State for the new dropdowns based on the updated logic ---
  const [configSummary, setConfigSummary] = useState({});
  const [selectedConfig, setSelectedConfig] = useState('');
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(false);

  const [variants, setVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState('');
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);

  // Existing state for the datasheet
  const [originalVariablesByDataset, setOriginalVariablesByDataset] = useState(null);
  const [groupedConfig, setGroupedConfig] = useState(null);
  const [originalValuesMap, setOriginalValuesMap] = useState(new Map());
  const [changedParams, setChangedParams] = useState(new Set());

  // State for the "Save As" modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [newConfigName, setNewConfigName] = useState('');
  const [description, setDescription] = useState('');
  const [customerVersion, setCustomerVersion] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState({
    show: false,
    title: '',
    message: '',
    variant: 'success' // can be 'success' or 'danger'
  });
  // 1. Fetch Releases
  useEffect(() => {
    const getReleases = async () => {
      setIsLoadingReleases(true);
      const cacheKey = "all-releases";
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        setReleaseIds(JSON.parse(cachedData).release_ids || []);
        setIsLoadingReleases(false);
        return;
      }
      try {
        const response = await api.get("/releases");
        sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
        setReleaseIds(response.data.release_ids || []);
      } catch {
        setError("Failed to fetch release IDs");
      } finally {
        setIsLoadingReleases(false);
      }
    };
    getReleases();
  }, []);

  // 2. Fetch Projects when a Release is selected
  useEffect(() => {
      isSelectionChanging.current = true;
      setError(null);
      setAvailableProjects([]);
      setSelectedProject('');
      setConfigSummary({});
      setSelectedConfig('');
      setVariants([]);
      setSelectedVariant('');
      if (selectedRelease) {
        const getProjects = async () => {
          setIsLoadingProjects(true);
          const cacheKey = `project-config-${selectedRelease}`;
          const cachedData = sessionStorage.getItem(cacheKey);

          if (cachedData) {
            const projects = JSON.parse(cachedData).projects || [];
            setAvailableProjects(projects);
            if (projects.length === 1) setSelectedProject(projects[0]);
            setIsLoadingProjects(false);
            return;
          }
          try {
            const response = await api.get(`/releases/${selectedRelease}/project-config`);
            const projects = response.data.projects || [];
            sessionStorage.setItem(cacheKey, JSON.stringify(response.data));
            setAvailableProjects(projects);
            if (projects.length === 1) setSelectedProject(projects[0]);
          } catch {
            setError(`Failed to fetch projects for ${selectedRelease}`);
          } finally {
            setIsLoadingProjects(false);
          }
          isSelectionChanging.current = false;
        };
        getProjects();
      } else {
        isSelectionChanging.current = false; // Unset if there's no selection
      }
  }, [selectedRelease]);

  // 3. Fetch Configs when a Project is selected
  useEffect(() => {
    isSelectionChanging.current = true; // Set the flag
    setError(null);
    // Reset all downstream selections
    setConfigSummary({});
    setSelectedConfig('');
    setVariants([]);
    setSelectedVariant('');
    if (selectedRelease && selectedProject) {
      setIsLoadingConfigs(true);
      api.get(`/releases/${selectedRelease}/config/${selectedProject}`)
        .then(response => setConfigSummary(response.data || {}))
        .catch(() => setError(`Failed to fetch configs for project ${selectedProject}`))
        .finally(() => {
          setIsLoadingConfigs(false);
          isSelectionChanging.current = false;
        });
    } else {
          isSelectionChanging.current = false; // Unset if there's no selection
    }
  }, [selectedRelease, selectedProject]);

  // 4. Fetch Variants when a Config is selected
  useEffect(() => {
    setVariants([]);
    setSelectedVariant('');
    if (selectedRelease && selectedProject && selectedConfig) {
      setIsLoadingVariants(true);
      api.get(`/releases/${selectedRelease}/config/${selectedProject}/${selectedConfig}/variants`)
        .then(response => {
          const variantData = response.data || [];
          setVariants(variantData);
          // Auto-select if there is only one variant
          if (variantData.length === 1) {
            setSelectedVariant(variantData[0]);
          }
        })
        .catch(() => setError(`Failed to fetch variants for config ${selectedConfig}`))
        .finally(() => setIsLoadingVariants(false));
    }
  }, [selectedRelease, selectedProject, selectedConfig]);

  // When a build/config is selected, fetch its content
  // 5. When a variant is selected, fetch its content
  useEffect(() => {
    // Clear previous data and errors immediately
    setGroupedConfig(null);
    setOriginalVariablesByDataset(null);
    setError(null);
    if (isSelectionChanging.current) {
      return;
    }
    if (selectedRelease && selectedProject && selectedConfig && selectedVariant) {
      const fetchConfigData = async () => {
        setIsLoadingData(true);
        try {
          const response = await api.get(`/releases/${selectedRelease}/config/${selectedProject}/${selectedConfig}/variants/${selectedVariant}`);
         
          setOriginalVariablesByDataset(response.data);
          const groupedData = parseAndGroupVariables(response.data);
          setGroupedConfig(groupedData);

          const valuesMap = new Map();
          Object.values(groupedData).flat().forEach(p => {
            valuesMap.set(p.name, p.value ?? p.default_value);
          });
          setOriginalValuesMap(valuesMap);
          setChangedParams(new Set());

        } catch (err) {
          setError(`Failed to load configuration for variant: ${selectedVariant}`);
        } finally {
          setIsLoadingData(false);
        }
      };
      fetchConfigData();
    }
  }, [selectedRelease, selectedProject, selectedConfig, selectedVariant]);

  const hasChanges = useMemo(() => changedParams.size > 0, [changedParams]);


  const handleParamChange = (paramToUpdate, newValue) => {
    const originalValue = originalValuesMap.get(paramToUpdate.name);
    const newChanged = new Set(changedParams);

    if (JSON.stringify(newValue) !== JSON.stringify(originalValue)) {
      newChanged.add(paramToUpdate.name);
    } else {
      newChanged.delete(paramToUpdate.name);
    }
    setChangedParams(newChanged);

    const groupName = paramToUpdate.display_group || 'scalar';
    setGroupedConfig(prev => {
      const newGroupedConfig = { ...prev };
      const groupParams = newGroupedConfig[groupName];
      if (groupParams) {
        const paramIndex = groupParams.findIndex(p => p.name === paramToUpdate.name);
        if (paramIndex !== -1) {
          const newGroupParams = [...groupParams];

          newGroupParams[paramIndex] = { ...newGroupParams[paramIndex], value: newValue };

          newGroupedConfig[groupName] = newGroupParams;
          return newGroupedConfig;
        }
      }
      return prev;
    });
  };

  const handleResetAll = () => {
    if (window.confirm("Are you sure you want to discard all changes?")) {
      if (originalVariablesByDataset) {
        const resetGroupedData = parseAndGroupVariables(originalVariablesByDataset);
        setGroupedConfig(resetGroupedData);
        setChangedParams(new Set()); // Clears all highlights
        alert('All changes have been reset.');
      }
    }
  };

  const handleResetGroup = (groupName) => {
    if (window.confirm(`Are you sure you want to reset the "${groupName}" group?`)) {
        if (!originalVariablesByDataset) return;
        const originalGroupedData = parseAndGroupVariables(originalVariablesByDataset);
        const originalGroup = originalGroupedData[groupName];
        if (originalGroup) {
            // Update the group's data
            setGroupedConfig(prev => ({ ...prev, [groupName]: originalGroup }));
            // Remove all params from this group from the changed set
            const newChanged = new Set(changedParams);
            originalGroup.forEach(param => newChanged.delete(param.name));
            setChangedParams(newChanged);
        }
      }
  };

  const handleResetParameter = (paramToReset) => {
    if (window.confirm(`Are you sure you want to reset the "${paramToReset.name}" parameter?`)) {
      const originalValue = originalValuesMap.get(paramToReset.name);
      if (originalValue !== undefined) {
        // Call the main change handler with the original value,
        handleParamChange(paramToReset, originalValue);
      }
    }
  };

  const handleCloseSaveModal = () => {
    setShowSaveModal(false);
    setNewConfigName('');
    setDescription('');
    setCustomerVersion('');
  };

  // Function to handle saving the configuration
  const executeSave = async () => {
    setIsSaving(true);

    const updatedVariablesByDataset = {};
    Object.values(groupedConfig).flat().forEach(p => {
        const { dataset, name, ...paramData } = p;
        if (!updatedVariablesByDataset[dataset]) {
            updatedVariablesByDataset[dataset] = {};
        }
        updatedVariablesByDataset[dataset][name] = paramData;
    });

    const savePayload = {
        base_variant: selectedVariant,
        updated_variables: updatedVariablesByDataset,
    };

    if (newConfigName) { savePayload.name = newConfigName; }
    if (description) { savePayload.description = description; }
    if (customerVersion) { savePayload.customer_version = customerVersion; }

    // Close the save modal immediately
    handleCloseSaveModal();

    try {
        await api.post(`/releases/${selectedRelease}/config/${selectedProject}/${selectedConfig}/variants/${selectedVariant}`, savePayload);

        // Show styled success modal instead of alert
        setFeedbackModal({
            show: true,
            title: 'Success!',
            message: 'Configuration saved successfully.',
            variant: 'success'
        });

        const response = await api.get(`/releases/${selectedRelease}/config/${selectedProject}`);
        setConfigSummary(response.data || {});

    } catch (err) {
        console.error("Failed to save configuration:", err);

        // Show styled error modal instead of alert
        setFeedbackModal({
            show: true,
            title: 'Error',
            message: 'Could not save configuration. Please check the console for details.',
            variant: 'danger'
        });
    } finally {
        setIsSaving(false);
    }
  };

  const handleOpenSaveModal = () => {
    setShowSaveModal(true);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
      <Row className="mb-4 align-items-end">
        <Col md>
          <Form.Group>
            <Form.Label>Select Base Release</Form.Label>
            <Form.Select value={selectedRelease} onChange={e => setSelectedRelease(e.target.value)} disabled={isLoadingReleases}>
              <option value="">{isLoadingReleases ? 'Loading...' : 'Select Release'}</option>
              {releaseIds.map(id => <option key={id} value={id}>{id}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md>
          <Form.Group>
            <Form.Label>Select Project</Form.Label>
            <Form.Select
              value={selectedProject}
              onChange={e => setSelectedProject(e.target.value)}
              disabled={!selectedRelease || isLoadingProjects || availableProjects.length <= 1}
            >
              <option value="">{isLoadingProjects ? 'Loading...' : 'Select Project'}</option>
              {availableProjects.map(proj => <option key={proj} value={proj}>{proj}</option>)}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md>
          <Form.Group>
            <Form.Label>Select Config</Form.Label>
            <Form.Select value={selectedConfig} onChange={e => setSelectedConfig(e.target.value)} disabled={!selectedProject || isLoadingConfigs}>
              <option value="">{isLoadingConfigs ? 'Loading...' : 'Select Config'}</option>
              {/* Map over the entries of the configSummary object */}
              {Object.entries(configSummary).map(([configId, configDetails]) => (
                <option key={configId} value={configId}>
                  {configDetails.name} {/* Display the name, send the ID */}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md>
          <Form.Group>
            <Form.Label>Select Variant</Form.Label>
            <Form.Select
              value={selectedVariant}
              onChange={e => setSelectedVariant(e.target.value)}
              disabled={!selectedConfig || isLoadingVariants}
            >
              <option value="">
              {isLoadingVariants ? 'Loading...' : 'Select Variant'}
              </option>
              {variants.map((variantName) => (
              <option key={variantName} value={variantName}>
                {variantName}
              </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      {/* 1. Show an error if it exists */}
      {error && (
              <Row>
                <Col>
                  <p style={{ color: 'red' }}>Error: {error}</p>
                </Col>
              </Row>
      )}
      {isLoadingData && !error && (
        <div className="text-center p-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading Configuration Data...</p>
        </div>
      )}

      {!isLoadingData && groupedConfig && !error && (
        <>
          <style>{`.selected-row td { background-color: #dbeafe !important; }`}</style>
          <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h1>Editing: {selectedVariant}</h1>
              <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleOpenSaveModal}
                    disabled={!hasChanges || isSaving}
                    style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer', backgroundColor: hasChanges ? '#007bff' : '#6c757d', color: 'white', border: 'none', borderRadius: '5px' }}
                  >
                    {isSaving ? 'Saving...' : 'Save Configuration'}
                  </button>
                  <button
                    onClick={handleResetAll}
                    disabled={!hasChanges || isSaving}
                    style={{ padding: '10px 20px', fontSize: '16px', cursor: 'pointer', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '5px' }}
                  >
                    Reset All
                  </button>
                </div>
            </div>
            {groupedConfig && Object.entries(groupedConfig)
              .filter(([groupName]) => groupName === 'scalar')
              .map(([groupName, parameters]) => (
                <div key={groupName} style={{ marginBottom: '40px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                    <div style={{display: 'flex', gap: '8px'}}>
                      <button onClick={() => handleResetGroup(groupName)} className="btn-danger">Reset</button>
                      </div>
                  </div>

                  <div
                    tabIndex="0"
                    style={{ /* Keep this wrapper div for focus/paste functionality if needed */
                      border: 'none', /* Remove border from here, moved to table */
                      boxShadow: 'none', /* Remove shadow from here */
                      padding: '0', /* Remove padding from here */
                      outline: 'none',
                      borderRadius: '0' /* Remove radius from here */
                    }}
                  >
                      <table className="scalar-datasheet">
                      <thead>
                        <tr>
                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left' }}>Name</th>
                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left' }}>Dataset</th>
                          <th style={{ padding: '8px', border: '1px solid #ccc', textAlign: 'left' }}>Brief</th>
                          <th style={{ textAlign: 'center' }}>Min</th> {/* Align headers */}
                          <th style={{ textAlign: 'center' }}>Max</th>
                          <th style={{ textAlign: 'center' }}>Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {parameters.map((param) => {
                          const isChanged = changedParams.has(param.name);
                          // This `key={param.name}` is important to help React efficiently re-render rows
                          const currentValue = param.value ?? param.default_value;
                          const isChangeable = param.modifiable ?? param.isChangeableForProject ?? false;
                          return (
                            <tr key={param.name} className={!isChangeable ? 'read-only-row' : 'changeable-row'}>
                              <td><span>{param.name}</span></td>
                              <td><span>{param.dataset}</span></td>
                              <td><span>{param.brief}</span></td>
                              <td><span>{param.min}</span></td>
                              <td><span>{param.max}</span></td>
                              <td>
                                {isChangeable ? (
                                  <EditableScalarCell
                                    param={param}
                                    isChanged={isChanged}
                                    onFinalChange={handleParamChange}
                                  />
                                ) : (
                                  <span style={{ padding: '10px 12px', display: 'block' }}>
                                    {param.value ?? param.default_value}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
            ))}
            {/* Part 2: Render EACH 'Lookup' parameter as its own separate table */}
            {groupedConfig?.['Lookup'] && groupedConfig['Lookup'].map(param => (
              <div key={param.name} style={{ marginBottom: '40px' }}>
                <h2>Parameter: {param.name}</h2>
                <button onClick={() => handleResetParameter(param)} className="btn-danger">Reset</button>
                <div style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', background: '#f9f9f9' }}>
                  <div style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                    <p style={{margin: '0 0 5px 0'}}><strong>Name:</strong> {param.name}</p>
                    <p style={{margin: '0 0 5px 0'}}><strong>Dataset:</strong> {param.dataset}</p>
                    <p style={{margin: 0}}><strong>Brief:</strong> {param.brief}</p>
                  </div>
                  <LookupTable param={param} onChange={handleParamChange} />
                </div>
              </div>
            ))}
            {/* Part 3: Render each 'Lookup2D' parameter */}
            {groupedConfig?.['Lookup2D'] && groupedConfig['Lookup2D'].map(param => (
              <div key={param.name} style={{ marginBottom: '40px' }}>
                <h2>Parameter: {param.name}</h2>
                <button onClick={() => handleResetParameter(param)} className="btn-danger">Reset</button>
                <div style={{ border: '1px solid #ccc', padding: '15px', borderRadius: '5px', background: '#f9f9f9' }}>
                  <div style={{ marginBottom: '10px', fontSize: '0.9rem' }}>
                    <p style={{margin: '0 0 5px 0'}}><strong>Dataset:</strong> {param.dataset}</p>
                    <p style={{margin: 0}}><strong>Brief:</strong> {param.brief}</p>
                  </div>
                  <Lookup2DTable param={param} onChange={handleParamChange} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      <Modal show={showSaveModal} onHide={() => setShowSaveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Save New Configuration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* New Config Name Input */}
          <Form.Group className="mb-3">
            <Form.Label>New Config Name</Form.Label>
            <Form.Control
              type="text"
              value={newConfigName}
              onChange={(e) => setNewConfigName(e.target.value)}
              placeholder="Optional: A unique name will be assigned if left empty."
            />
          </Form.Group>

          {/* Description Input */}
          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder=""
            />
          </Form.Group>

          {/* Customer Version Input */}
          <Form.Group className="mb-3">
            <Form.Label>Customer Version (Optional)</Form.Label>
            <Form.Control
              type="text"
              value={customerVersion}
              onChange={(e) => setCustomerVersion(e.target.value)}
              placeholder=""
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={isSaving}
            onClick={executeSave} // No longer needs to pass a filename
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={feedbackModal.show} onHide={() => setFeedbackModal({ ...feedbackModal, show: false })}>
          <Modal.Header closeButton className={`bg-${feedbackModal.variant} text-white`}>
              <Modal.Title>{feedbackModal.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              {feedbackModal.message}
          </Modal.Body>
          <Modal.Footer>
              <Button variant="primary" onClick={() => setFeedbackModal({ ...feedbackModal, show: false })}>
                  OK
              </Button>
          </Modal.Footer>
      </Modal>
    </div>
  );
}

export default ConfigDatasheet;